import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  useMediaQuery,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";

// More compact styled components for modal use
const StyledCard = styled(Card)(({ theme, color = "#368AD0" }) => ({
  height: "100%",
  borderRadius: "12px",
  backgroundColor: "#222",
  transition: "transform 0.2s ease-in-out",
  border: `1px solid ${color}20`,
  position: "relative",
  overflow: "hidden",
  "&:before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "3px",
    background: color
  },
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: `0 6px 12px ${color}30`
  }
}));

const Title = styled(Typography)(({ theme, color = "#368AD0" }) => ({
  color: color,
  fontWeight: 600,
  marginBottom: "8px",
  padding: "4px 2px 8px 2px",
  borderBottom: `1px solid ${color}40`,
  fontSize: "1rem"
}));

const PointItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: "4px",
  padding: "2px 0"
}));

const PointBullet = styled(Box)(({ theme, color = "#368AD0" }) => ({
  width: 6,
  height: 6,
  borderRadius: "50%",
  backgroundColor: color,
  marginTop: "7px",
  marginRight: "8px",
  flexShrink: 0
}));

const MaxPoints = styled(Typography)(({ theme, color = "#368AD0" }) => ({
  marginTop: "8px",
  fontWeight: 600,
  color: color,
  fontSize: "0.875rem"
}));

// FAQ styled components
const FaqCard = styled(Card)(({ theme }) => ({
  background: "linear-gradient(145deg, #272727, #1c1c1c)",
  borderRadius: "12px",
  marginTop: "16px",
  border: "1px solid rgba(255,255,255,0.05)"
}));

const FaqListItem = styled(ListItem)(({ theme }) => ({
  paddingLeft: 0,
  paddingRight: 0,
  marginBottom: "4px"
}));

const FaqListItemIcon = styled(ListItemIcon)(({ theme, iconcolor }) => ({
  minWidth: "32px",
  "& svg": {
    height: "22px",
    width: "22px"
  },
  color: iconcolor
}));

const FaqListItemText = styled(ListItemText)(({ theme }) => ({
  margin: 0,
  "& .MuiListItemText-primary": {
    color: "#fff",
    fontSize: "0.875rem",
    lineHeight: 1.5
  }
}));

const PointsSystem = ({ type }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const systems = [
    {
      title: "#800gChallenge",
      points: [
        "1 point for every 100 grams logged, max 8 points per day",
        ...(!type || type === "BOTH"
          ? ["2 points for each lesson completed"]
          : [])
      ],
      maxPoints: 300,
      color: "#F6541C" // Orange/Red
    },
    {
      title: "Lazy Macros",
      points: [
        "1 point for every 100 grams logged, max 8 points per day",
        "2 points for hitting 80% of protein target",
        "Additional 2 points for hitting 100% of protein target",
        ...(!type || type === "BOTH"
          ? ["2 points for each lesson completed"]
          : [])
      ],
      maxPoints: 420,
      color: "#368AD0" // Blue
    },
    {
      title: "Three Pillars Method",
      points: [
        "1 point for every 100 grams logged, max 8 points per day",
        "2 points for hitting 80% of protein target",
        "Additional 2 points for hitting 100% of protein target",
        "4 points for logging >1,000 calories on Days 6-40",
        ...(!type || type === "BOTH"
          ? ["2 points for each lesson completed"]
          : [])
      ],
      maxPoints: 700,
      color: "#FF9431" // Orange
    }
  ];

  const faqItems = [
    {
      text: "Today's points are posted once a day with the calendar date change.",
      icon: <CalendarTodayOutlinedIcon sx={{ height: "18px !important" }} />,
      color: "#F6541C"
    },
    {
      text: "If you update prior days, those points are awarded immediately.",
      icon: <UpdateOutlinedIcon />,
      color: "#368AD0"
    },
    {
      text: "Points for daily program content are dripped out at a maximum of 2 points per day across the challenge dates (even if you started or completed the program ahead of the start date).",
      icon: <InfoOutlinedIcon />,
      color: "#FF9431"
    },
    {
      text: "The tiebreak is your average #800gChallenge weight consumed.",
      icon: <BalanceOutlinedIcon />,
      color: "#4CAF50"
    }
  ];

  return (
    <Box
      sx={{
        maxHeight: "60vh",
        overflowY: "auto",
        // WebkitOverflowScrolling: "touch",
        pr: 1, // Small right padding to accommodate scrollbar
        "&::-webkit-scrollbar": {
          width: "2px"
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          borderRadius: "4px"
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(0, 0, 0, 0.1)"
        }
      }}
    >
      <Grid container spacing={2}>
        {systems.map((system, index) => (
          <Grid item xs={12} key={system.title} sx={{ mb: 0.5 }}>
            <StyledCard elevation={2} color={system.color}>
              <CardContent sx={{ p: 2 }}>
                <Title variant="subtitle1" color={system.color}>
                  {system.title}
                </Title>
                <Box>
                  {system.points.map((point, idx) => (
                    <PointItem key={idx}>
                      <PointBullet color={system.color} />
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#eee",
                          fontSize: "0.875rem",
                          lineHeight: 1.5
                        }}
                      >
                        {point}
                      </Typography>
                    </PointItem>
                  ))}
                  {type === "BOTH" && (
                    <MaxPoints variant="body2" color={system.color}>
                      Max points = {system.maxPoints}
                    </MaxPoints>
                  )}
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}

        {/* FAQ Section */}
        <Grid item xs={12} sx={{ pb: 2 }}>
          <FaqCard elevation={3}>
            <CardContent sx={{ p: "0px !important" }}>
              <Box
                sx={{
                  background:
                    "linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                  borderRadius: "8px",
                  p: 1.5,
                  border: "1px solid rgba(255,255,255,0.05)"
                }}
              >
                <List disablePadding>
                  {faqItems.map((item, index) => (
                    <React.Fragment key={index}>
                      <FaqListItem alignItems="center">
                        <FaqListItemIcon iconcolor={item.color}>
                          {item.icon}
                        </FaqListItemIcon>
                        <FaqListItemText primary={item.text} />
                      </FaqListItem>
                      {index < faqItems.length - 1 && (
                        <Divider
                          variant="inset"
                          component="li"
                          sx={{
                            borderColor: "rgba(255,255,255,0.05)",
                            ml: 4,
                            mr: 0
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            </CardContent>
          </FaqCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PointsSystem;
